// Puerto verbatim de packages/hce/src/hce/consentimiento/vaultClient.ts —
// sin cambios de lógica, solo ajuste de imports (config.ts y auditedFetch.ts
// viven en lib/ en este paquete). Cliente de las rutas de la bóveda
// (firma electrónica, entrega de copia por WhatsApp, registro de recetas)
// — nunca pasan por Medplum, van directo a config.vaultUrl con el Bearer de
// sesión (cabecerasBoveda).
import type { EspecificacionPdf } from '@logiacare/hce-pdf';
import { getConfig } from '../lib/config';
import { cabecerasBoveda, obtenerTenantSlug } from '../lib/audit/auditedFetch';

export function base64DeBytes(bytes: Uint8Array): string {
  const CHUNK = 0x8000;
  let binario = '';
  for (let i = 0; i < bytes.length; i += CHUNK) {
    binario += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(binario);
}

function bytesDeBase64(b64: string): Uint8Array {
  const binario = atob(b64);
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i++) bytes[i] = binario.charCodeAt(i);
  return bytes;
}

async function vaultUrlOLanza(): Promise<{ url: string; tenantSlug: string }> {
  const config = getConfig();
  if (!config.vaultUrl) {
    throw new Error('La bóveda no está configurada (MEDPLUM_APP_VAULT_URL).');
  }
  const tenantSlug = await obtenerTenantSlug();
  if (!tenantSlug) {
    throw new Error('No se pudo resolver el consultorio (tenantSlug) para esta operación.');
  }
  return { url: config.vaultUrl, tenantSlug };
}

export interface FirmarDocumentoInput {
  documentoId: string;
  /**
   * "consentimiento"/"receta"/"acta-entrega-recepcion" tienen su propio
   * nombre de acción de auditoría en la bóveda. Desde el Rediseño mayor
   * 2026-08-31 (Módulo H) también acepta cualquier código de formulario
   * MSP/ACESS o "nota-clinica" — la bóveda los anota bajo una acción
   * genérica (`hce-formulario-sign`) con el código real en el detalle.
   */
  tipoDocumento?: 'consentimiento' | 'receta' | 'acta-entrega-recepcion' | (string & {});
  /** Contenido del documento — el servidor arma el PDF (2026-09-12). Sin nombre/registro/fecha del profesional: los pone el servidor. */
  especificacion: EspecificacionPdf;
  /** Solo 117/116-cert: cédula del profesional (el servidor la ignora en cualquier otro tipoDocumento). */
  cedula?: string;
}

export interface FirmarDocumentoResult {
  signedPdf: Uint8Array;
  signatureRef: string;
  firmadoEn: string;
  simulada: boolean;
  /** Solo receta común: URL del portal de validación con el payload del QR, armada por el servidor. */
  urlValidacion?: string;
}

/**
 * El servidor arma el PDF desde `especificacion`, le pone nombre/matrícula/
 * fecha de la sesión (y en la receta el QR con su hash) y lo firma. Antes el
 * PDF se armaba acá y `/api/hce/sign` solo lo sellaba — ese endpoint se
 * retiró con el legado packages/hce (2026-09-13, responde 410).
 */
export async function firmarDocumentoEnServidor(input: FirmarDocumentoInput): Promise<FirmarDocumentoResult> {
  const { url, tenantSlug } = await vaultUrlOLanza();
  const res = await fetch(`${url}/api/hce/firmar-documento`, {
    method: 'POST',
    headers: cabecerasBoveda(),
    body: JSON.stringify({
      tenantSlug,
      documentoId: input.documentoId,
      tipoDocumento: input.tipoDocumento ?? 'consentimiento',
      especificacion: input.especificacion,
      cedula: input.cedula,
    }),
  });
  if (!res.ok) {
    const cuerpo = await res.json().catch(() => ({}));
    throw new Error(cuerpo.error || `La bóveda rechazó la firma (HTTP ${res.status}).`);
  }
  const cuerpo = await res.json();
  return {
    signedPdf: bytesDeBase64(cuerpo.signedPdfBase64),
    signatureRef: cuerpo.signatureRef,
    firmadoEn: cuerpo.firmadoEn,
    simulada: cuerpo.simulada,
    urlValidacion: typeof cuerpo.urlValidacion === 'string' ? cuerpo.urlValidacion : undefined,
  };
}

export interface EntregarCopiaInput {
  patientRef: string;
  practitionerName?: string;
  filename?: string;
  pdf: Uint8Array;
}

export interface EntregarCopiaResult {
  status: 'sent' | 'skipped' | 'error';
  detail?: string;
  waLink?: string;
}

export async function entregarCopia(input: EntregarCopiaInput): Promise<EntregarCopiaResult> {
  const { url, tenantSlug } = await vaultUrlOLanza();
  const res = await fetch(`${url}/api/hce/deliver-copy`, {
    method: 'POST',
    headers: cabecerasBoveda(),
    body: JSON.stringify({
      tenantSlug,
      patientRef: input.patientRef,
      practitionerName: input.practitionerName,
      filename: input.filename,
      pdfBase64: base64DeBytes(input.pdf),
    }),
  });
  if (!res.ok) {
    const cuerpo = await res.json().catch(() => ({}));
    throw new Error(cuerpo.error || `La bóveda rechazó la entrega (HTTP ${res.status}).`);
  }
  return res.json();
}

/** "Reclasificar ahora" (Modo Auditor sección B, 2026-09-10) — fuerza el mismo cálculo activo/pasivo del barrido nocturno para un paciente puntual. `estado: null` = sin actividad clínica todavía, nada que clasificar. */
export interface ReclasificarArchivoResult {
  estado: 'activo' | 'pasivo' | null;
  cambio?: boolean;
  mensaje?: string;
}

export async function reclasificarArchivoDePaciente(patientId: string): Promise<ReclasificarArchivoResult> {
  const { url, tenantSlug } = await vaultUrlOLanza();
  const res = await fetch(`${url}/api/hce/archivo/reclasificar`, {
    method: 'POST',
    headers: cabecerasBoveda(),
    body: JSON.stringify({ tenantSlug, patientId }),
  });
  const cuerpo = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(cuerpo.error || `La bóveda rechazó la reclasificación (HTTP ${res.status}).`);
  }
  return cuerpo;
}

export interface EmitirRecetaInput {
  qrToken: string;
  /** Uno o más — AM 1124 Art. 5 permite varios medicamentos por receta con una sola firma (2026-09-02). */
  medicationRequestRefs: string[];
  medicamentoDisplay: string;
  sustanciaControlada: boolean;
  signatureRef?: string;
  // Sin prescriptorDisplay ni payloadHash (2026-09-12): el prescriptor y el
  // hash del QR los decide el servidor (este último lo calculó al firmar).
}

export function nuevoQrToken(): string {
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

export async function emitirReceta(input: EmitirRecetaInput): Promise<void> {
  const { url, tenantSlug } = await vaultUrlOLanza();
  const res = await fetch(`${url}/api/hce/emitir-receta`, {
    method: 'POST',
    headers: cabecerasBoveda(),
    body: JSON.stringify({
      tenantSlug,
      qrToken: input.qrToken,
      medicationRequestRefs: input.medicationRequestRefs,
      medicamentoDisplay: input.medicamentoDisplay,
      sustanciaControlada: input.sustanciaControlada,
      signatureRef: input.signatureRef,
    }),
  });
  if (!res.ok) {
    const cuerpo = await res.json().catch(() => ({}));
    throw new Error(cuerpo.error || `La bóveda rechazó el registro de la receta (HTTP ${res.status}).`);
  }
}

/** Razón social y RUC de "Datos del consultorio" — solo lectura (a diferencia de `prepararReceta`, no consume un correlativo). */
export interface DatosConsultorio {
  razonSocial: string;
  ruc: string;
}

let consultorioEnCurso: { sesion: string; promesa: Promise<DatosConsultorio> } | null = null;

/**
 * Una sola petición por sesión: el encabezado MSP la pide en cada formulario que se abre. La caché va atada al
 * Bearer de la sesión (no es global de la pestaña): al cerrar sesión y entrar a otro consultorio en la misma
 * pestaña no debe servirse la razón social del anterior. Un fallo no se cachea.
 */
export function obtenerDatosConsultorio(): Promise<DatosConsultorio> {
  const sesion = cabecerasBoveda().Authorization ?? '';
  if (!consultorioEnCurso || consultorioEnCurso.sesion !== sesion) {
    const promesa = (async () => {
      const { url } = await vaultUrlOLanza();
      const res = await fetch(`${url}/api/hce/consultorio`, { headers: cabecerasBoveda() });
      if (!res.ok) throw new Error(`La bóveda rechazó la lectura de los datos del consultorio (HTTP ${res.status}).`);
      return (await res.json()) as DatosConsultorio;
    })();
    const entrada = { sesion, promesa };
    consultorioEnCurso = entrada;
    promesa.catch(() => {
      if (consultorioEnCurso === entrada) consultorioEnCurso = null;
    });
  }
  return consultorioEnCurso.promesa;
}

export interface DatosPreparacionReceta {
  numero: string;
  ciudad: string;
  ruc: string;
  razonSocial: string;
  direccion: string;
  telefono: string;
  correo: string;
}

/** "Receta digital" (checklist MSP/ACESS) — se llama justo antes de firmar: asigna el número correlativo (mutación atómica del lado del servidor) y resuelve ciudad/RUC/razón social. */
export async function prepararReceta(): Promise<DatosPreparacionReceta> {
  const { url } = await vaultUrlOLanza();
  const res = await fetch(`${url}/api/hce/recetas/preparar`, { method: 'POST', headers: cabecerasBoveda() });
  if (!res.ok) {
    const cuerpo = await res.json().catch(() => ({}));
    throw new Error(cuerpo.error || `La bóveda rechazó la preparación de la receta (HTTP ${res.status}).`);
  }
  return res.json();
}

export interface RegistrarActaFirmadaInput {
  signatureRef: string;
  firmadoEn: string;
  practitionerName?: string;
}

export async function registrarActaFirmada(input: RegistrarActaFirmadaInput): Promise<void> {
  const { url, tenantSlug } = await vaultUrlOLanza();
  const res = await fetch(`${url}/api/hce/licencia/acta`, {
    method: 'POST',
    headers: cabecerasBoveda(),
    body: JSON.stringify({ tenantSlug, signatureRef: input.signatureRef, firmadoEn: input.firmadoEn, practitionerName: input.practitionerName }),
  });
  if (!res.ok) {
    const cuerpo = await res.json().catch(() => ({}));
    throw new Error(cuerpo.error || `La bóveda rechazó el registro del Acta (HTTP ${res.status}).`);
  }
}

// -----------------------------------------------------------------------------
// Envío digital de documentos (Fase 3/4 de "receta digital") — puente
// Medplum -> `patients` local por cédula, y el envío en sí (WhatsApp/correo).
// -----------------------------------------------------------------------------

export interface PacienteVinculado {
  patientId: number;
  nombre: string;
  phone: string | null;
  email: string | null;
  tieneEmail: boolean;
}

/** `null` si la cédula no tiene coincidencia en el directorio local (paciente nunca dado de alta en el panel CRM). */
export async function obtenerPacienteVinculado(cedula: string): Promise<PacienteVinculado | null> {
  const { url } = await vaultUrlOLanza();
  // H-001: la cédula va en el cuerpo (POST), nunca en la URL.
  const res = await fetch(`${url}/api/hce/paciente-vinculado`, {
    method: 'POST',
    headers: cabecerasBoveda(),
    body: JSON.stringify({ cedula }),
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const cuerpo = await res.json().catch(() => ({}));
    throw new Error(cuerpo.error || `La bóveda rechazó la búsqueda del paciente (HTTP ${res.status}).`);
  }
  return res.json();
}

export type CanalEnvioDocumento = 'whatsapp' | 'email';

export interface EnviarDocumentoDigitalInput {
  cedula: string;
  canales: CanalEnvioDocumento[];
  pdf: Uint8Array;
  fileName?: string;
  /** "Receta médica", "Resultado de laboratorio", "Certificado médico"... — va en el asunto del correo y el pie del WhatsApp. */
  etiquetaDocumento: string;
  /** URL pública opcional (ej. `/farmacia/:token` de una receta) — ausente para documentos sin portal de validación propio. */
  link?: string;
  practitionerName?: string;
}

export interface ResultadoCanalEnvio {
  status: 'sent' | 'skipped' | 'error';
  detail: string;
}

export interface EnviarDocumentoDigitalResultado {
  ok: boolean;
  resultados: Partial<Record<CanalEnvioDocumento, ResultadoCanalEnvio>>;
}

export async function enviarDocumentoDigital(input: EnviarDocumentoDigitalInput): Promise<EnviarDocumentoDigitalResultado> {
  const { url, tenantSlug } = await vaultUrlOLanza();
  const res = await fetch(`${url}/api/hce/documentos/enviar`, {
    method: 'POST',
    headers: cabecerasBoveda(),
    body: JSON.stringify({
      tenantSlug,
      cedula: input.cedula,
      canales: input.canales,
      pdfBase64: base64DeBytes(input.pdf),
      fileName: input.fileName,
      etiquetaDocumento: input.etiquetaDocumento,
      link: input.link,
      practitionerName: input.practitionerName,
    }),
  });
  const cuerpo = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(cuerpo.error || `La bóveda rechazó el envío del documento (HTTP ${res.status}).`);
  }
  return cuerpo;
}
